import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiUser, FiCpu, FiBridge, FiStar} = FiIcons;

const CoreOfferingSection = () => {
  return (
    <section className="py-20 relative" id="instructor">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-indigo-900/30 to-slate-900"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
        >
          <h2 className="text-4xl md:text-5xl font-light text-white mb-8">
            ไม่ว่าคุณจะอยู่บนเส้นทางใด <br />
            <span className="text-indigo-400">ปลายทางคือความเข้าใจในศาสตร์ปาจื้อ (八字,Bazi) เดียวกัน</span>
          </h2>
        </motion.div>

        {/* Instructor Story */}
        <motion.div
          className="bg-gradient-to-br from-indigo-900/20 to-purple-900/20 rounded-3xl p-12 border border-indigo-500/20 backdrop-blur-sm mb-16"
          initial={{opacity: 0, scale: 0.9}}
          whileInView={{opacity: 1, scale: 1}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
        >
          <div className="text-center">
            {/* Story Content */}
            <div className="max-w-4xl mx-auto">
              <h3 className="text-3xl font-light text-white mb-6">
                ที่มาแห่งศาสตร์: การเดินทางของผู้เชื่อมสองโลก
              </h3>
              <div className="space-y-6 text-slate-300 font-light leading-relaxed">
                <p>
                  หลักสูตรนี้ถือกำเนิดจากจิตวิญญาณของ <span className="text-indigo-400 font-medium">อาจารย์รวิ อัญญากาญจน์ (fengshuiX)</span> — ผู้ที่เริ่มต้นจากการเป็นวิศวกรคอมพิวเตอร์ ผู้เชื่อมั่นในโลกแห่งตรรกะและเหตุผล
                </p>
                <p>
                  แต่ด้วยชะตาฟ้าลิขิต เขาได้เดินทางสู่โลกเร้นลับแห่งศาสตร์ <span className="text-purple-400 font-medium">ปาจื้อ (八字,Bazi)</span> และค้นพบว่า... ทั้งสองโลกไม่ได้ขัดแย้งกัน แต่คือภาพสะท้อนของกันและกัน
                </p>
                <p>
                  เขาคือ <span className="text-indigo-400 font-medium">"สถาปนิกผู้ถอดรหัส Bazi"</span> ผู้ใช้เวลากว่าทศวรรษในการสร้างสะพานเชื่อมระหว่าง "ปัญญาญาณโบราณ" และ "เทคโนโลยีสมัยใหม่"
                </p>
              </div>
              <div className="mt-8 p-6 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl border border-indigo-400/20">
                <p className="text-indigo-200 font-light leading-relaxed">
                  จนกลายเป็นแอปพลิเคชัน <span className="text-white font-medium">"Bazi by FengshuiX"</span> และกลั่นกรองแก่นแท้ทั้งหมดลงในหลักสูตรนี้... เพื่อส่งต่อให้กับผู้ที่พร้อมจะเรียนรู้
                </p>
              </div>

              {/* Credentials Display */}
              <div className="mt-8 flex items-center justify-center mb-6">
                <SafeIcon icon={FiCpu} className="w-6 h-6 text-indigo-400 mr-3" />
                <span className="text-indigo-200 font-light">Engineer → Bazi Master</span>
              </div>
              <div className="h-px bg-gradient-to-r from-transparent via-indigo-400 to-transparent mb-6"></div>
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="bg-indigo-400/20 rounded-xl py-3 text-sm text-indigo-200 font-light">Logic</div>
                <div className="bg-purple-400/20 rounded-xl py-3 text-sm text-purple-200 font-light">Intuition</div>
                <div className="bg-blue-400/20 rounded-xl py-3 text-sm text-blue-200 font-light">Synthesis</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Master Statement */}
        <motion.div
          className="text-center bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-lg rounded-3xl p-10 border border-white/20"
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.3}}
          viewport={{once: true}}
        >
          <div className="flex items-center justify-center mb-6">
            <SafeIcon icon={FiStar} className="w-8 h-8 text-yellow-400 mr-3" />
            <h4 className="text-2xl font-light text-white">Bazi Master</h4>
            <SafeIcon icon={FiStar} className="w-8 h-8 text-yellow-400 ml-3" />
          </div>
          <p className="text-xl text-slate-200 font-light leading-relaxed max-w-4xl mx-auto">
            คุณจะได้เรียนรู้โดยตรงจาก <span className="text-indigo-400 font-medium">"Bazi Master"</span> ผู้ที่จะสอนให้คุณเข้าใจ <span className="text-purple-400 font-medium">"ตรรกะเบื้องหลังความขลัง"</span> และมอบเครื่องมือที่เขาพัฒนาขึ้นเอง ให้คุณใช้เป็นอาวุธคู่กายไปตลอดชีวิต
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default CoreOfferingSection;